import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { UI, makeButton, makeCloseButton } from '../utils/ui-button.js';
import { netManager } from '../net/index.js';
import { saveParty } from '../net/api.js';

// ── PC Box — store/retrieve beasts beyond the 6-slot party. ──
// A mouse/touch overlay that pauses the world. Left column = your team (max 6), right grid = the box.
// Tap a team beast to deposit it; tap a stored beast to withdraw it. Caps: the team can never drop to
// 0 and never exceed 6 — the box is unbounded. Both lists persist via the local save (autosaved).

const MAX_PARTY = 6;
const PAGE_SIZE = 12; // 2 columns x 6 rows of box chips per page

/**
 * @typedef PcBoxSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

export class PcBoxScene extends BaseScene {
  /** @type {PcBoxSceneData} */ #sceneData;
  /** @type {number} */ #page = 0;
  /** @type {Phaser.GameObjects.Text} */ #status;
  /** @type {Phaser.GameObjects.Container} */ #layer;
  #panel = { left: 0, top: 0, w: 0, h: 0 };

  constructor() {
    super({ key: SCENE_KEYS.PC_BOX_SCENE });
  }

  /** @param {PcBoxSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
    this.#page = 0;
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;
    this.add.rectangle(0, 0, width, height, 0x05080d, 0.8).setOrigin(0);

    const panelW = Math.min(840, width - 48);
    const panelH = Math.min(600, height - 48);
    const left = width / 2 - panelW / 2;
    const top = height / 2 - panelH / 2;
    this.#panel = { left, top, w: panelW, h: panelH };

    const g = this.add.graphics();
    g.fillStyle(UI.glass, 0.97);
    g.fillRoundedRect(left, top, panelW, panelH, 18);
    g.lineStyle(2, UI.emerald, 0.6);
    g.strokeRoundedRect(left, top, panelW, panelH, 18);

    this.add
      .text(width / 2, top + 34, '📦 PC BOX', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '32px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    makeCloseButton(this, left + panelW - 34, top + 34, () => this.#close());

    this.#status = this.add
      .text(width / 2, top + panelH - 26, 'Tap a team beast to store it · tap a stored beast to add it to your team.', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '14px',
        color: UI.subHex,
      })
      .setOrigin(0.5);

    this.#layer = this.add.container(0, 0);
    this.#render();
    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  /** @returns {import('../types/typedef.js').Monster[]} */
  #party() {
    return dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || [];
  }

  /** @returns {import('../types/typedef.js').Monster[]} */
  #box() {
    return dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE) || [];
  }

  #setStatus(msg) {
    if (this.#status) this.#status.setText(msg);
  }

  /**
   * Persist the AUTHORITATIVE team + box to the server NOW. The box scene runs with the world scene
   * paused, so its 20s autosave doesn't fire — without this, a deposit/withdraw was only saved if the
   * world autosave happened to run later, and a refresh in between LOST the beast (the reported bug).
   * Sends the box even when EMPTY (a deliberate emptying must clear the server box, not resurrect it).
   * @returns {void}
   */
  #persist() {
    dataManager.saveData(); // local copy first
    void saveParty(netManager.wallet, this.#party(), this.#box()).catch(() => {});
  }

  /** Move a party beast → box. Refuses to empty the team. */
  #deposit(index) {
    const party = [...this.#party()];
    if (party.length <= 1) {
      this.#setStatus('You must keep at least one beast on your team.');
      return;
    }
    const [mon] = party.splice(index, 1);
    const box = [...this.#box(), mon];
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, party);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE, box);
    this.#setStatus(`${mon.name ?? 'Beast'} was sent to the box.`);
    this.#persist();
    this.#render();
  }

  /** Move a box beast → party. Refuses past 6. */
  #withdraw(index) {
    const party = [...this.#party()];
    if (party.length >= MAX_PARTY) {
      this.#setStatus('Your team is full (6). Store one first.');
      return;
    }
    const box = [...this.#box()];
    const [mon] = box.splice(index, 1);
    party.push(mon);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, party);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE, box);
    this.#setStatus(`${mon.name ?? 'Beast'} joined your team!`);
    this.#persist();
    // keep the page valid if it just emptied
    const pages = Math.max(1, Math.ceil(box.length / PAGE_SIZE));
    if (this.#page > pages - 1) this.#page = pages - 1;
    this.#render();
  }

  /** A clickable beast chip (name + Lv). */
  #chip(x, y, w, h, mon, color, onTap) {
    const c = this.add.container(x, y);
    const bg = this.add.graphics();
    bg.fillStyle(UI.glass, 0.96);
    bg.fillRoundedRect(0, 0, w, h, 5);
    bg.lineStyle(3, UI.ink, 1);
    bg.strokeRoundedRect(0, 0, w, h, 5);
    bg.lineStyle(1.5, color, 1);
    bg.strokeRoundedRect(2.5, 2.5, w - 5, h - 5, 3);
    const name = this.add.text(12, 8, `${mon.name ?? 'Beast'}`, {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '16px',
      color: UI.textHex,
    });
    const lvl = this.add.text(12, 28, `Lv. ${mon.currentLevel ?? 1}`, {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      fontSize: '13px',
      color: UI.goldHex,
    });
    c.add([bg, name, lvl]);
    const zone = this.add.zone(0, 0, w, h).setOrigin(0).setInteractive({ useHandCursor: true });
    zone.on(Phaser.Input.Events.POINTER_OVER, () => bg.setAlpha(0.8));
    zone.on(Phaser.Input.Events.POINTER_OUT, () => bg.setAlpha(1));
    zone.on(Phaser.Input.Events.POINTER_DOWN, onTap);
    c.add(zone);
    this.#layer.add(c);
    return c;
  }

  #render() {
    this.#layer.removeAll(true);
    const { left, top, w, h } = this.#panel;
    const party = this.#party();
    const box = this.#box();
    const colW = (w - 60) / 2;
    const colTop = top + 78;
    const chipH = 50;
    const chipGap = 8;

    // ── left: TEAM ──
    const lx = left + 20;
    this.#layer.add(
      this.add.text(lx, colTop - 26, `YOUR TEAM  ${party.length}/${MAX_PARTY}`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '18px',
        color: UI.emeraldHex,
      })
    );
    party.forEach((mon, i) => {
      this.#chip(lx, colTop + i * (chipH + chipGap), colW, chipH, mon, UI.emerald, () => this.#deposit(i));
    });

    // ── right: BOX ──
    const rx = left + 40 + colW;
    this.#layer.add(
      this.add.text(rx, colTop - 26, `PC BOX  (${box.length})`, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '18px',
        color: UI.goldHex,
      })
    );
    if (box.length === 0) {
      this.#layer.add(
        this.add.text(rx, colTop + 10, 'Empty — catch beasts while your\nteam is full and they land here.', {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '14px',
          color: UI.subHex,
        })
      );
      return;
    }
    const pages = Math.ceil(box.length / PAGE_SIZE);
    const start = this.#page * PAGE_SIZE;
    const pageItems = box.slice(start, start + PAGE_SIZE);
    const gridColW = (colW - chipGap) / 2;
    pageItems.forEach((mon, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const cx = rx + col * (gridColW + chipGap);
      const cy = colTop + row * (chipH + chipGap);
      this.#chip(cx, cy, gridColW, chipH, mon, UI.gold, () => this.#withdraw(start + idx));
    });

    // page controls
    if (pages > 1) {
      const py = top + h - 64;
      const prev = makeButton(this, {
        x: rx + 50,
        y: py,
        width: 80,
        height: 34,
        label: '◀',
        onClick: () => {
          if (this.#page > 0) {
            this.#page -= 1;
            this.#render();
          }
        },
      });
      const next = makeButton(this, {
        x: rx + colW - 50,
        y: py,
        width: 80,
        height: 34,
        label: '▶',
        onClick: () => {
          if (this.#page < pages - 1) {
            this.#page += 1;
            this.#render();
          }
        },
      });
      const pageLbl = this.add
        .text(rx + colW / 2, py, `${this.#page + 1}/${pages}`, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '16px',
          color: UI.textHex,
        })
        .setOrigin(0.5);
      this.#layer.add([prev, next, pageLbl]);
    }
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  #close() {
    this.#persist(); // flush the authoritative team + box to the server on the way out
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.PC_BOX_SCENE);
    if (this.scene.isPaused(prev)) this.scene.resume(prev);
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
